import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  LayoutDashboard, Users, CalendarDays, UsersRound, Send,
  CreditCard, FileText, Clock, Palmtree, CalendarCheck,
  BarChart3, Award, UserCircle, Settings, ChevronRight, ChevronLeft,
  X, Play, SkipForward, Check, BookOpen, FileDown, HelpCircle,
  RefreshCw, Trophy, XCircle, CheckCircle
} from 'lucide-react';

const COMMON_STEPS = [
  { navTarget: 'nav-panel-główny', route: '/', icon: LayoutDashboard, title: 'Panel główny', description: 'Tu znajdziesz podsumowanie: nadchodzące wyjazdy, zaległe płatności i powiadomienia.' },
  { navTarget: 'nav-klienci', route: '/clients', icon: Users, title: 'Klienci', description: 'Baza klientów biura. Dodawaj, edytuj i filtruj kontakty. Możesz importować dane z Excela.' },
  { navTarget: 'nav-rezerwacje', route: '/bookings', icon: CalendarDays, title: 'Rezerwacje', description: 'Zarządzaj rezerwacjami klientów. Twórz nowe rezerwacje i śledź statusy płatności.' },
  { navTarget: 'nav-wyjazdy-grupowe', route: '/group-trips', icon: UsersRound, title: 'Wyjazdy grupowe', description: 'Organizuj wyjazdy grupowe: uczestnicy, rooming list, płatności i komunikacja w jednym miejscu.' },
  { navTarget: 'nav-sms-/-whatsapp', route: '/messages', icon: Send, title: 'SMS / WhatsApp', description: 'Wysyłaj wiadomości SMS i WhatsApp do klientów. Używaj szablonów i śledzenie historii.' },
  { navTarget: 'nav-płatności', route: '/payments', icon: CreditCard, title: 'Płatności', description: 'Rejestruj wpłaty klientów, śledź zaległości i generuj potwierdzenia.' },
  { navTarget: 'nav-faktury', route: '/invoices', icon: FileText, title: 'Faktury', description: 'Twórz faktury VAT, VAT-Marża, proformy i korekty. Wysyłaj PDF emailem.' },
  { navTarget: 'nav-czas-pracy', route: '/work-hours', icon: Clock, title: 'Czas pracy', description: 'Rejestruj godziny pracy. Administratorzy mogą generować raporty miesięczne.' },
  { navTarget: 'nav-urlopy', route: '/leaves', icon: Palmtree, title: 'Urlopy', description: 'Składaj wnioski urlopowe i sprawdzaj bilans dni wolnych.' },
  { navTarget: 'nav-kalendarz', route: '/calendar', icon: CalendarCheck, title: 'Kalendarz', description: 'Podgląd urlopów zespołu, spotkań i wydarzeń w widoku miesięcznym lub tygodniowym.' },
];

const ADMIN_STEPS = [
  { navTarget: 'nav-raporty', route: '/reports', icon: BarChart3, title: 'Raporty', description: 'Generuj raporty sprzedażowe i analizy wyników biura.' },
  { navTarget: 'nav-premie', route: '/commissions', icon: Award, title: 'Premie', description: 'Ranking sprzedawców, naliczanie prowizji i bonusów za wyniki.' },
  { navTarget: 'nav-pracownicy', route: '/users', icon: UserCircle, title: 'Pracownicy', description: 'Zarządzaj kontami pracowników, rolami i uprawnieniami dostępu.' },
  { navTarget: 'nav-ustawienia', route: '/settings', icon: Settings, title: 'Ustawienia', description: 'Konfiguracja firmy, SMTP, SMSAPI i innych integracji systemu.' },
];

const QUIZ_QUESTIONS = [
  {
    question: 'Gdzie dodasz nowego klienta do systemu?',
    options: [
      { label: 'A', text: 'Rezerwacje' },
      { label: 'B', text: 'Klienci' },
      { label: 'C', text: 'Ustawienia' },
    ],
    correct: 'B',
    explanation: 'Nowych klientów dodajemy w sekcji Klienci — tam znajdziesz przycisk „Dodaj klienta".',
  },
  {
    question: 'Jak wyślesz SMS do uczestników wyjazdu grupowego?',
    options: [
      { label: 'A', text: 'SMS / WhatsApp → Nowa wiadomość' },
      { label: 'B', text: 'Wyjazdy grupowe → Szczegóły → zakładka Komunikacja' },
      { label: 'C', text: 'Panel główny → Powiadomienia' },
    ],
    correct: 'B',
    explanation: 'Wejdź w szczegóły wyjazdu grupowego i kliknij zakładkę Komunikacja — stamtąd wyślesz SMS do wszystkich.',
  },
  {
    question: 'Gdzie sprawdzisz zaległe płatności klientów?',
    options: [
      { label: 'A', text: 'Panel główny — sekcja powiadomień' },
      { label: 'B', text: 'Kalendarz' },
      { label: 'C', text: 'Urlopy' },
    ],
    correct: 'A',
    explanation: 'Na Panelu głównym w sekcji powiadomień widzisz zaległe płatności i inne ważne alerty.',
  },
  {
    question: 'Co robisz po rozmowie z klientem, który nie podjął decyzji?',
    options: [
      { label: 'A', text: 'Nic — czekam' },
      { label: 'B', text: 'Wysyłam ofertę lub wiadomość przypominającą' },
      { label: 'C', text: 'Usuwam klienta z systemu' },
    ],
    correct: 'B',
    explanation: 'Zawsze warto wrócić do klienta i przypomnieć się z ofertą — to podstawa sprzedaży!',
  },
];

export default function OnboardingTour() {
  const { user, refreshUser, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [phase, setPhase] = useState('idle');
  const [currentStep, setCurrentStep] = useState(0);
  const [tooltipPos, setTooltipPos] = useState({ top: 0, left: 0 });
  const [highlightRect, setHighlightRect] = useState(null);
  const tooltipRef = useRef(null);

  // Quiz state
  const [quizIndex, setQuizIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [quizAnswers, setQuizAnswers] = useState([]);
  const [showExplanation, setShowExplanation] = useState(false);

  const steps = isAdmin ? [...COMMON_STEPS, ...ADMIN_STEPS] : COMMON_STEPS;

  useEffect(() => {
    if (!user) return;
    if (user.role === 'demo') return;
    if (user.onboarding_completed) return;

    const savedStep = user.onboarding_step || 0;
    if (savedStep > 0 && savedStep < steps.length) {
      setCurrentStep(savedStep);
      setPhase('tour');
    } else {
      setPhase('welcome');
    }
  }, [user, steps.length]);

  const positionTooltip = useCallback(() => {
    if (phase !== 'tour') return;
    const step = steps[currentStep];
    if (!step) return;

    const el = document.querySelector(`[data-testid="${step.navTarget}"]`);
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const newHighlightRect = { top: rect.top, left: rect.left, width: rect.width, height: rect.height };
    const tooltipTop = Math.max(60, Math.min(rect.top - 20, window.innerHeight - 300));
    const newTooltipPos = { top: tooltipTop, left: rect.right + 16 };

    setHighlightRect(prev => {
      if (!prev || prev.top !== newHighlightRect.top || prev.left !== newHighlightRect.left) return newHighlightRect;
      return prev;
    });
    setTooltipPos(prev => {
      if (prev.top !== newTooltipPos.top || prev.left !== newTooltipPos.left) return newTooltipPos;
      return prev;
    });
  }, [phase, currentStep, steps]);

  useEffect(() => {
    positionTooltip();
    window.addEventListener('resize', positionTooltip);
    return () => window.removeEventListener('resize', positionTooltip);
  }, [positionTooltip]);

  const saveProgress = async (step) => {
    try { await api.post('/auth/save-onboarding-progress', { step }); } catch { /* silent */ }
  };

  const completeOnboarding = async () => {
    try { await api.post('/auth/complete-onboarding'); await refreshUser(); } catch { /* silent */ }
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      const next = currentStep + 1;
      setCurrentStep(next);
      saveProgress(next);
      navigate(steps[next].route);
    } else {
      setPhase('complete');
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      const prev = currentStep - 1;
      setCurrentStep(prev);
      saveProgress(prev);
      navigate(steps[prev].route);
    }
  };

  const handleSkip = async () => {
    setPhase('idle');
    await completeOnboarding();
    toast.info('Szkolenie pominięte. Możesz je uruchomić ponownie w Moje Konto.');
  };

  const handleStart = () => {
    setCurrentStep(0);
    setPhase('tour');
    navigate(steps[0].route);
    saveProgress(0);
  };

  const handleFinish = async () => {
    setPhase('idle');
    await completeOnboarding();
    navigate('/');
    toast.success('Onboarding zakończony!');
  };

  // Quiz handlers
  const startQuiz = () => {
    setQuizIndex(0);
    setSelectedAnswer(null);
    setQuizAnswers([]);
    setShowExplanation(false);
    setPhase('quiz');
  };

  const handleAnswer = (label) => {
    if (showExplanation) return;
    setSelectedAnswer(label);
    setShowExplanation(true);
  };

  const handleNextQuestion = () => {
    const isCorrect = selectedAnswer === QUIZ_QUESTIONS[quizIndex].correct;
    const newAnswers = [...quizAnswers, isCorrect];
    setQuizAnswers(newAnswers);
    setSelectedAnswer(null);
    setShowExplanation(false);

    if (quizIndex < QUIZ_QUESTIONS.length - 1) {
      setQuizIndex(quizIndex + 1);
    } else {
      const score = newAnswers.filter(Boolean).length;
      setPhase('quiz-result');
      api.post('/auth/save-quiz-result', { score, total: QUIZ_QUESTIONS.length }).catch(() => {});
    }
  };

  const retryQuiz = () => {
    startQuiz();
  };

  if (phase === 'idle') return null;

  // ===== WELCOME SCREEN =====
  if (phase === 'welcome') {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="onboarding-welcome">
        <div className="absolute inset-0 bg-navy-900/70 backdrop-blur-sm" />
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
          <div className="bg-gradient-to-br from-navy-800 to-navy-900 p-8 text-center">
            <div className="w-16 h-16 mx-auto bg-gold-400/20 rounded-2xl flex items-center justify-center mb-4">
              <BookOpen className="w-8 h-8 text-gold-400" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2" data-testid="welcome-title">Witamy w Aisha Travel CRM</h1>
            <p className="text-navy-200 text-sm">Przeprowadzimy Cię przez podstawowe funkcje systemu</p>
          </div>
          <div className="p-6 space-y-4">
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-sm text-slate-600 leading-relaxed">
                Szkolenie zajmie ok. <strong>1-2 minuty</strong>. Pokażemy Ci najważniejsze sekcje CRM
                za pomocą lekkich podpowiedzi — możesz jednocześnie klikać i testować system.
              </p>
            </div>
            <div className="text-xs text-slate-400 text-center">
              {isAdmin ? `${COMMON_STEPS.length + ADMIN_STEPS.length} kroków + quiz` : `${COMMON_STEPS.length} kroków + quiz`}
            </div>
            <div className="flex flex-col gap-2">
              <Button onClick={handleStart} className="w-full bg-teal-600 hover:bg-teal-700 h-11 text-base" data-testid="start-onboarding-btn">
                <Play className="w-4 h-4 mr-2" />Rozpocznij szkolenie
              </Button>
              <Button onClick={handleSkip} variant="ghost" className="w-full text-slate-500" data-testid="skip-onboarding-btn">
                <SkipForward className="w-4 h-4 mr-2" />Pomiń szkolenie
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ===== COMPLETE SCREEN (after tour, before quiz) =====
  if (phase === 'complete') {
    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="onboarding-complete">
        <div className="absolute inset-0 bg-navy-900/70 backdrop-blur-sm" />
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
          <div className="bg-gradient-to-br from-emerald-600 to-teal-700 p-8 text-center">
            <div className="w-16 h-16 mx-auto bg-white/20 rounded-full flex items-center justify-center mb-4">
              <Check className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2" data-testid="complete-title">Szkolenie ukończone!</h1>
            <p className="text-emerald-100">Teraz sprawdź swoją wiedzę w krótkim quizie</p>
          </div>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <a href="https://www.youtube.com/@aishatravel" target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2 p-3 border rounded-xl hover:bg-slate-50 transition-colors text-sm text-slate-700"
                data-testid="watch-video-btn">
                <Play className="w-5 h-5 text-red-500" />
                <span>Obejrzyj wideo szkoleniowe</span>
              </a>
              <button
                onClick={() => toast.info('Instrukcja PDF będzie dostępna wkrótce')}
                className="flex items-center gap-2 p-3 border rounded-xl hover:bg-slate-50 transition-colors text-sm text-slate-700"
                data-testid="download-pdf-btn">
                <FileDown className="w-5 h-5 text-blue-500" />
                <span>Pobierz instrukcję PDF</span>
              </button>
            </div>
            <div className="bg-slate-50 rounded-xl p-4 text-center">
              <HelpCircle className="w-6 h-6 text-teal-600 mx-auto mb-2" />
              <p className="text-sm text-slate-600">{QUIZ_QUESTIONS.length} pytania o codzienną pracę w CRM</p>
            </div>
            <Button onClick={startQuiz} className="w-full bg-teal-600 hover:bg-teal-700 h-11 text-base" data-testid="start-quiz-btn">
              <HelpCircle className="w-4 h-4 mr-2" />Rozpocznij quiz
            </Button>
            <Button onClick={handleFinish} variant="ghost" className="w-full text-slate-500" data-testid="skip-quiz-btn">
              <SkipForward className="w-4 h-4 mr-2" />Pomiń quiz
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ===== QUIZ SCREEN =====
  if (phase === 'quiz') {
    const q = QUIZ_QUESTIONS[quizIndex];
    const isCorrect = selectedAnswer === q.correct;

    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="onboarding-quiz">
        <div className="absolute inset-0 bg-navy-900/70 backdrop-blur-sm" />
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
          {/* Progress */}
          <div className="h-1.5 bg-slate-100">
            <div className="h-full bg-teal-500 transition-all duration-500" style={{ width: `${((quizIndex + 1) / QUIZ_QUESTIONS.length) * 100}%` }} />
          </div>

          <div className="p-6 space-y-5">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
                  <HelpCircle className="w-4 h-4 text-teal-600" />
                </div>
                <span className="text-sm font-medium text-slate-500" data-testid="quiz-step-counter">
                  Pytanie {quizIndex + 1} z {QUIZ_QUESTIONS.length}
                </span>
              </div>
              <button onClick={handleFinish} className="text-slate-400 hover:text-slate-600" data-testid="close-quiz-btn">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Question */}
            <h2 className="text-lg font-semibold text-slate-800 leading-snug" data-testid="quiz-question">
              {q.question}
            </h2>

            {/* Options */}
            <div className="space-y-2">
              {q.options.map(opt => {
                let optionClass = 'border-slate-200 hover:border-teal-300 hover:bg-teal-50/50';
                if (showExplanation) {
                  if (opt.label === q.correct) {
                    optionClass = 'border-emerald-400 bg-emerald-50';
                  } else if (opt.label === selectedAnswer && !isCorrect) {
                    optionClass = 'border-red-400 bg-red-50';
                  } else {
                    optionClass = 'border-slate-200 opacity-50';
                  }
                } else if (selectedAnswer === opt.label) {
                  optionClass = 'border-teal-400 bg-teal-50';
                }

                return (
                  <button
                    key={opt.label}
                    onClick={() => handleAnswer(opt.label)}
                    disabled={showExplanation}
                    className={`w-full text-left p-3.5 rounded-xl border-2 transition-all duration-200 flex items-center gap-3 ${optionClass}`}
                    data-testid={`quiz-option-${opt.label}`}
                  >
                    <span className={`w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                      showExplanation && opt.label === q.correct ? 'bg-emerald-500 text-white' :
                      showExplanation && opt.label === selectedAnswer && !isCorrect ? 'bg-red-500 text-white' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {showExplanation && opt.label === q.correct ? <CheckCircle className="w-4 h-4" /> :
                       showExplanation && opt.label === selectedAnswer && !isCorrect ? <XCircle className="w-4 h-4" /> :
                       opt.label}
                    </span>
                    <span className="text-sm font-medium text-slate-700">{opt.text}</span>
                  </button>
                );
              })}
            </div>

            {/* Explanation */}
            {showExplanation && (
              <div className={`p-4 rounded-xl text-sm ${isCorrect ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200'}`} data-testid="quiz-explanation">
                <div className="flex items-start gap-2">
                  {isCorrect
                    ? <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                    : <XCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />}
                  <div>
                    <p className={`font-semibold ${isCorrect ? 'text-emerald-700' : 'text-amber-700'}`}>
                      {isCorrect ? 'Poprawnie!' : 'Niepoprawnie'}
                    </p>
                    <p className={`mt-0.5 ${isCorrect ? 'text-emerald-600' : 'text-amber-600'}`}>{q.explanation}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Next button */}
            {showExplanation && (
              <Button onClick={handleNextQuestion} className="w-full bg-teal-600 hover:bg-teal-700" data-testid="quiz-next-btn">
                {quizIndex < QUIZ_QUESTIONS.length - 1 ? (
                  <>Następne pytanie<ChevronRight className="w-4 h-4 ml-1" /></>
                ) : (
                  <>Zobacz wynik<Trophy className="w-4 h-4 ml-1" /></>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ===== QUIZ RESULT =====
  if (phase === 'quiz-result') {
    const score = quizAnswers.filter(Boolean).length;
    const total = QUIZ_QUESTIONS.length;
    const perfect = score === total;
    const good = score >= total - 1;

    return (
      <div className="fixed inset-0 z-[100] flex items-center justify-center" data-testid="quiz-result-screen">
        <div className="absolute inset-0 bg-navy-900/70 backdrop-blur-sm" />
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 overflow-hidden">
          <div className={`p-8 text-center ${perfect ? 'bg-gradient-to-br from-emerald-500 to-teal-600' : good ? 'bg-gradient-to-br from-amber-500 to-orange-500' : 'bg-gradient-to-br from-slate-600 to-slate-700'}`}>
            <div className="w-16 h-16 mx-auto bg-white/20 rounded-full flex items-center justify-center mb-4">
              <Trophy className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-1" data-testid="quiz-score">
              {score}/{total}
            </h1>
            <p className="text-white/90 text-lg font-medium" data-testid="quiz-message">
              {perfect ? 'Świetnie! Perfekcyjny wynik!' : good ? 'Prawie idealnie! Dobra robota!' : 'Spróbuj jeszcze raz — dasz radę!'}
            </p>
          </div>
          <div className="p-6 space-y-3">
            {/* Answer summary */}
            <div className="space-y-2 mb-4">
              {QUIZ_QUESTIONS.map((q, i) => (
                <div key={i} className="flex items-center gap-3 text-sm">
                  {quizAnswers[i]
                    ? <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    : <XCircle className="w-5 h-5 text-red-500 flex-shrink-0" />}
                  <span className={quizAnswers[i] ? 'text-slate-700' : 'text-slate-500'}>{q.question}</span>
                </div>
              ))}
            </div>

            {!perfect && (
              <Button onClick={retryQuiz} variant="outline" className="w-full border-teal-200 text-teal-700 hover:bg-teal-50" data-testid="retry-quiz-btn">
                <RefreshCw className="w-4 h-4 mr-2" />Powtórz quiz
              </Button>
            )}
            <Button onClick={handleFinish} className="w-full bg-teal-600 hover:bg-teal-700 h-11 text-base" data-testid="go-to-dashboard-btn">
              Przejdź do panelu
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ===== TOUR TOOLTIP =====
  const step = steps[currentStep];
  const StepIcon = step.icon;
  const progress = ((currentStep + 1) / steps.length) * 100;

  return (
    <>
      <div className="fixed inset-0 z-[90] pointer-events-none" data-testid="onboarding-tour-overlay">
        {highlightRect && (
          <div
            className="absolute border-2 border-teal-400 rounded-lg transition-all duration-300 shadow-[0_0_0_4000px_rgba(0,0,0,0.08)]"
            style={{ top: highlightRect.top - 3, left: highlightRect.left - 3, width: highlightRect.width + 6, height: highlightRect.height + 6 }}
          />
        )}
      </div>

      <div
        ref={tooltipRef}
        className="fixed z-[100] w-80 animate-in fade-in slide-in-from-left-3 duration-300"
        style={{ top: tooltipPos.top, left: tooltipPos.left }}
        data-testid="onboarding-tooltip"
      >
        <div className="bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden">
          <div className="h-1 bg-slate-100">
            <div className="h-full bg-teal-500 transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
          <div className="flex items-center justify-between px-4 pt-3 pb-1">
            <span className="text-xs font-medium text-slate-400" data-testid="step-counter">Krok {currentStep + 1} z {steps.length}</span>
            <button onClick={handleSkip} className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="skip-tour-btn"><X className="w-4 h-4" /></button>
          </div>
          <div className="px-4 pb-3">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <StepIcon className="w-4 h-4 text-teal-600" />
              </div>
              <h3 className="font-semibold text-slate-800" data-testid="step-title">{step.title}</h3>
            </div>
            <p className="text-sm text-slate-500 leading-relaxed" data-testid="step-description">{step.description}</p>
          </div>
          <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-t">
            <Button variant="ghost" size="sm" onClick={handlePrev} disabled={currentStep === 0} className="text-slate-500" data-testid="prev-step-btn">
              <ChevronLeft className="w-4 h-4 mr-1" />Wstecz
            </Button>
            <Button size="sm" onClick={handleNext} className="bg-teal-600 hover:bg-teal-700" data-testid="next-step-btn">
              {currentStep === steps.length - 1 ? <><Check className="w-4 h-4 mr-1" />Zakończ</> : <>Dalej<ChevronRight className="w-4 h-4 ml-1" /></>}
            </Button>
          </div>
        </div>
        <div className="absolute top-6 -left-2 w-4 h-4 bg-white border-l border-b border-slate-200 transform rotate-45" />
      </div>
    </>
  );
}
